import { MobXProviderContext } from 'mobx-react';
import type { ConversationModel } from 'Models/ConversationModel';
import {
  FALLBACK_CONTACT,
  FALLBACK_CONVERSATION,
} from 'Modules/conversation/deleteConversation/constants';
import {
  resolvePersonContactName,
  resolvePhoneContactName,
} from 'Modules/conversation/deleteConversation/resolver';
import React from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';

/**
 * Resolves the display name for a conversation, mirroring how the chat
 * list/header names conversations in the app.
 *
 * - Group chats with an auto-generated topic ("Group--") derive a name
 *   from participants (excluding the current user).
 * - Group chats with a custom topic use that topic directly.
 * - 1:1 chats resolve the other participant's name via phone or personId.
 */
const resolveConversationDisplayName = (
  conversation: ConversationModel | undefined,
  personStore: RootStoreProps['person'],
  contactStore: RootStoreProps['contact'],
  conversationStore: RootStoreProps['conversation']
): string => {
  if (!conversation) {
    return '';
  }

  if (conversation.grouping !== 'OneOnOne') {
    // Auto-generated group topics start with "Group--"; derive a name
    // from the other participants instead of showing the raw topic.
    if (
      conversation.grouping === 'Group' &&
      conversation.topic?.startsWith('Group--')
    ) {
      return conversationStore.createProperName(
        conversation.participants.filter(
          (participant) => participant.personId !== personStore.loggedInPersonId
        )
      );
    }

    // Custom (user-set) group topic. Use as-is.
    return conversation.topic || FALLBACK_CONVERSATION;
  }

  // 1:1 conversation. Resolve the other participant's contact name.
  const otherPtc = conversation.participants.find(
    ({ personId }) => personId !== personStore.loggedInPersonId
  );

  if (!otherPtc) {
    return conversation.topic || FALLBACK_CONTACT;
  }

  if (otherPtc.phone) {
    return resolvePhoneContactName(otherPtc, contactStore, personStore);
  }

  if (otherPtc.personId && otherPtc.personId > 0) {
    return resolvePersonContactName(otherPtc, personStore, conversation.topic);
  }

  return conversation.topic || FALLBACK_CONTACT;
};

// Returns the display name for a conversation.
export const useConversationDisplayName = (
  conversation?: ConversationModel
): string => {
  const {
    person: personStore,
    contact: contactStore,
    conversation: conversationStore,
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  return resolveConversationDisplayName(
    conversation,
    personStore,
    contactStore,
    conversationStore
  );
};